import { useQuery } from '@tanstack/react-query';
import { AxiosInstance } from '../../../../../utilities/requestClient';

export type RaptorLibraryFolder = {
  name: string;
  order?: number;
};

export type RaptorLibraryResponse = RaptorLibraryFolder[] | string[];

export type RaptorLibraryFolderFilesIndex = {
  folder: string;
  files: string[];
};

export const fetchRaptorLibraryFolders =
  async (): Promise<RaptorLibraryResponse> => {
    const response =
      await AxiosInstance.get<RaptorLibraryResponse>('raptor_library');
    return response.data;
  };

export const fetchRaptorLibraryFiles = async (
  folder: string,
): Promise<RaptorLibraryResponse> => {
  const response = await AxiosInstance.get<RaptorLibraryResponse>(
    `raptor_library/${folder}`,
  );
  return response.data;
};

/**
 * Hook to fetch raptor library folders
 */
export function useRaptorLibraryFolders() {
  return useQuery({
    queryKey: ['raptor-library-folders'],
    queryFn: fetchRaptorLibraryFolders,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Hook to fetch raptor library files for a specific folder
 */
export function useRaptorLibraryFiles(folder: string) {
  return useQuery({
    queryKey: ['raptor-library-files', folder],
    queryFn: () => fetchRaptorLibraryFiles(folder),
    enabled: folder !== '',
    staleTime: 60 * 60 * 1000,
  });
}

function getFolderNamesFromResponse(
  data: RaptorLibraryResponse | undefined,
): string[] {
  if (!data || data.length === 0) {
    return [];
  }

  if (typeof data[0] === 'string') {
    return data as string[];
  }

  return (data as RaptorLibraryFolder[]).map((item) => item.name);
}

/**
 * Hook to fetch all library files for all folders, once folders are loaded.
 * This is useful for building a fuzzy-search index across the whole library.
 */
export function useRaptorLibraryAllFiles(
  foldersData: RaptorLibraryResponse | undefined,
) {
  const folderNames = getFolderNamesFromResponse(foldersData);

  return useQuery({
    queryKey: ['raptor-library-all-files', folderNames],
    enabled: folderNames.length > 0,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<RaptorLibraryFolderFilesIndex[]> => {
      const results = await Promise.all(
        folderNames.map((folder) =>
          fetchRaptorLibraryFiles(folder) as Promise<RaptorLibraryResponse>,
        ),
      );

      return folderNames.map((folder, index) => {
        const filesResponse = results[index];
        const filesArray =
          Array.isArray(filesResponse) && filesResponse.length > 0
            ? typeof filesResponse[0] === 'string'
              ? (filesResponse as string[])
              : (filesResponse as Array<{ name: string }>).map(
                  (item) => item.name,
                )
            : [];

        return {
          folder,
          files: filesArray,
        };
      });
    },
  });
}
